import { Link } from 'react-router-dom';
import { Shield, BrainCircuit, LifeBuoy, ChevronRight, CheckCircle2, Sparkles } from 'lucide-react';
import { usePageMeta, useFAQSchema } from '../../../hooks/useSEO';
import BreadcrumbSchema from '../../../components/seo/BreadcrumbSchema';
import { FAQSection, FinalCTA } from './DubaiCostOfLivingPage';

const EDGES = [
  {
    icon: Shield, title: 'MEA-Approved Trust',
    copy: 'Registered overseas recruitment licence B-0762/DEL/PER/1000+/5/9148/2015 issued by India\u2019s Ministry of External Affairs. Your fees flow into an MEA-audited channel, your file is built to government-grade standards, and your contract is enforceable in Indian courts.',
  },
  {
    icon: BrainCircuit, title: 'Migration Intelligence Edge',
    copy: 'We don\u2019t recommend courses — we map them. Every Dubai programme in our shortlist is cross-referenced with UAE MoHRE shortage occupations, DIFC + ADGM hiring panels, and post-2026 Golden Visa eligibility criteria. The result: a degree that doubles as a residency pathway.',
  },
  {
    icon: LifeBuoy, title: 'Post-Landing Support',
    copy: 'Most consultants vanish after the visa stamp. ZAFCO sticks around for the first 90 days: airport pickup brief, KHDA campus orientation, Emirates ID checklist, MoHRE work-permit application, bank-account opening, and a personalised job-search playbook tied to your degree.',
  },
];

const FAQS = [
  { q: 'Is ZAFCO an MEA-approved consultancy?', a: 'Yes — ZAFCO holds MEA Recruitment Licence B-0762/DEL/PER/1000+/5/9148/2015 issued by the Ministry of External Affairs, Government of India. We are listed on the official MEA Protector of Emigrants registry.' },
  { q: 'How does ZAFCO\u2019s "Migration Intelligence" differ from other consultancies?', a: 'We map every course recommendation to live UAE labour-market data — MoHRE shortage lists, DIFC fintech hiring indices, and Golden Visa eligibility tracks. Other consultancies recommend courses based on which universities pay them; we recommend based on where you will be employable in 2030.' },
  { q: 'What is included in ZAFCO\u2019s 90-day Post-Landing Support?', a: 'Airport pickup brief, KHDA campus orientation walk-through, Emirates ID and medical fitness scheduling, MoHRE student work-permit filing, free bank account introduction (Emirates NBD / Mashreq), 1:1 LinkedIn refresh, and a structured job-search playbook with weekly check-ins for the first 12 weeks.' },
  { q: 'Do you offer a refund if my Dubai student visa is rejected?', a: 'Yes — our standard contract carries a refund guarantee on government visa rejections that are not caused by candidate fraud or document misrepresentation. Specific terms are detailed in your engagement letter at sign-up.' },
];

export default function DubaiWhyChooseZafcoPage() {
  usePageMeta(
    'Why Choose ZAFCO for Dubai Study Abroad — MEA-Approved + Migration Intelligence',
    'ZAFCO\u2019s 3-edge advantage for Dubai 2026: MEA-approved licensure, Migration Intelligence (course-to-shortage-occupation mapping), and 90-day post-landing support including MoHRE work-permit filing.'
  );
  useFAQSchema(FAQS);

  return (
    <div className="zaf-edu-platform bg-white text-slate-900" data-testid="dubai-why-choose-zafco-page">
      <BreadcrumbSchema items={[
        { name: 'Home', url: 'https://zafco.in/' },
        { name: 'Study Abroad', url: 'https://zafco.in/study-abroad/' },
        { name: 'Study in Dubai', url: 'https://zafco.in/study-abroad/dubai/' },
        { name: 'Why Choose ZAFCO', url: 'https://zafco.in/study-abroad/dubai/why-choose-zafco/' },
      ]} />

      <section className="bg-gradient-to-br from-[#0F1B2C] to-[#162538] text-white py-12 md:py-16">
        <div className="container max-w-5xl mx-auto px-4 sm:px-6 md:px-12">
          <Link to="/study-abroad/dubai/" className="inline-flex items-center gap-2 text-[#D4AF37] text-xs font-semibold uppercase tracking-widest mb-4 hover:text-amber-300">
            <ChevronRight className="w-3 h-3 rotate-180" /> Dubai Hub
          </Link>
          <h1 className="text-3xl md:text-5xl font-bold font-syne leading-tight">Why Choose ZAFCO for Dubai — <span className="text-[#D4AF37]">3 Edges That Compound</span></h1>
          <p className="mt-5 text-slate-300 max-w-3xl leading-relaxed">Pick a Dubai consultancy that thinks 5 years ahead — not just to the first visa stamp. MEA-approved governance, course-to-occupation intelligence, and 90 days of in-country support combine into a measurably higher residency-conversion outcome.</p>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="container max-w-6xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="grid md:grid-cols-3 gap-5">
            {EDGES.map((e) => (
              <article key={e.title} className="bg-white border border-slate-200 rounded-2xl p-6 hover:border-[#D4AF37] hover:shadow-md transition-all" data-testid={`edge-${e.title.split(' ')[0].toLowerCase()}`}>
                <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/15 flex items-center justify-center mb-3"><e.icon className="w-6 h-6 text-[#D4AF37]" /></div>
                <h3 className="font-bold font-syne text-[#0F1B2C] mb-2 text-lg">{e.title}</h3>
                <p className="text-sm text-slate-700 leading-relaxed">{e.copy}</p>
              </article>
            ))}
          </div>

          <div className="mt-12 bg-gradient-to-br from-amber-50 via-white to-amber-50 border border-[#D4AF37]/40 rounded-2xl p-6 md:p-8">
            <div className="flex items-center gap-2 text-[#D4AF37] mb-2"><Sparkles className="w-5 h-5" /><span className="text-xs font-bold uppercase tracking-widest">The Intelligence Layer</span></div>
            <h2 className="text-2xl md:text-3xl font-bold font-syne mb-3">From "Where Should I Apply?" to "Which Visa Do I Land On?"</h2>
            <p className="text-slate-700 leading-relaxed max-w-4xl">For every Dubai shortlist, we attach a <strong>residency-pathway matrix</strong>: target employer cluster → graduation visa (Job Seeker / Green / Golden) → fast-track conditions (GPA, salary band, employer tier). The plan ships with your offer letter. No surprises, no Plan-B regrets in Year 3.</p>

            <h3 className="text-base font-bold text-[#0F1B2C] mt-6 mb-3">What gets mapped, per candidate:</h3>
            <ul className="grid sm:grid-cols-2 gap-2.5 text-sm">
              {[
                'Course → MoHRE Skill-Shortage Occupation match',
                'Tuition cluster → Realistic part-time income offset',
                'Internship cluster → DIFC / DMC / Internet City pipeline',
                'GPA band → Golden Visa specialist-talent eligibility',
                'Salary band → Green Visa qualification post-graduation',
                'Spouse/family plan → Dependent visa cost-of-living model',
              ].map((b) => <li key={b} className="flex gap-2 text-slate-800"><CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" /> {b}</li>)}
            </ul>
          </div>
        </div>
      </section>

      <FAQSection faqs={FAQS} />
      <FinalCTA source="dubai-why-choose-zafco" />
    </div>
  );
}
